import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig, AxiosHeaders } from 'axios';
import { showToast } from '../components/Toast/Toast';
import { ToastTypes } from '../components/Toast/Toast.types';
import { DetailedError, DetailedErrorType, ErrorWithMessage } from '../types/error.types';
import { QueryClient } from '@tanstack/react-query';

let timeoutId: NodeJS.Timeout | undefined;
let suppressTimeoutToast = false;

export const suppressApiTimeoutToast = () => {
  suppressTimeoutToast = true;
};

export const enableApiTimeoutToast = () => {
  suppressTimeoutToast = false;
};

export const requestInterceptor = (apiClient: AxiosInstance) => (config: InternalAxiosRequestConfig) => {
  const requestMessage = () => {
    const isStoreGroupOperation = config.url?.includes('/bundle-groups');
    const isBundleUpchargesOperation = config.url?.includes('/bundle-upcharges');

    if (!isStoreGroupOperation && !isBundleUpchargesOperation && !suppressTimeoutToast) {
      showToast({
        toastType: ToastTypes.Info,
        label: 'Request processing, please be patient',
        subtext: 'Check back later to see updates',
        id: 'apiGatewayTimeout',
      });
    }
    timeoutId = undefined;
  };
  if (timeoutId) clearTimeout(timeoutId);

  const isBundleUpchargesOperation = config.url?.includes('/bundle-upcharges');
  if (!isBundleUpchargesOperation) {
    timeoutId = setTimeout(requestMessage, 30000);
  }

  if (apiClient.defaults.headers.common.Authorization === undefined) {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken !== null) {
      // eslint-disable-next-line no-param-reassign
      apiClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
      const headersMaybe = config.headers as unknown;
      const headers = headersMaybe as AxiosHeaders;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (headers && typeof (headers as any).set === 'function') {
        headers.set('Authorization', `Bearer ${accessToken}`);
      } else if (config.headers && typeof config.headers === 'object') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (config.headers as any)['Authorization'] = `Bearer ${accessToken}`;
      }
    } else {
      // eslint-disable-next-line no-param-reassign
      delete apiClient.defaults.headers?.common.Authorization;
    }
  }

  return config;
};

interface ErrorWithRetry extends InternalAxiosRequestConfig {
  retry?: boolean;
}

export const errorResponseInterceptor =
  (apiClient: AxiosInstance, disableAuth: boolean, queryClient: QueryClient) => async (error: AxiosError) => {
    if (error.code === 'ERR_CANCELED') {
      return;
    }
    if (error.code === 'ERR_NETWORK' || error.response?.status === 504) {
      const error504Mesg = 'API Gateway request timed out';
      throw new Error(error504Mesg);
    }
    const originalRequest = error.config as ErrorWithRetry;

    if (error?.response?.status === 401 && !originalRequest?.retry && originalRequest?.url !== '/refresh-token') {
      if (timeoutId) clearTimeout(timeoutId);
      /* istanbul ignore next */
      if (disableAuth) return;
      originalRequest.retry = true;
      const refreshToken = localStorage.getItem('refreshToken');
      // eslint-disable-next-line no-param-reassign
      apiClient.defaults.headers.common.Authorization = undefined;
      const refreshResponse = await apiClient.post('/refresh-token', refreshToken, {
        transformRequest: (data, headers) => {
          headers.set('authorization', undefined);
          return data;
        },
      });
      const newAuthToken = refreshResponse.data;
      localStorage.setItem('accessToken', newAuthToken);
      // eslint-disable-next-line no-param-reassign
      apiClient.defaults.headers.common.Authorization = `Bearer ${newAuthToken}`;
      queryClient.resetQueries();
      const retryRequest = {
        ...originalRequest,
        headers: {
          ...originalRequest?.headers,
          Authorization: `Bearer ${newAuthToken}`,
        },
      };
      apiClient(retryRequest);
      // if we receive a 401 after refreshing the token or receive any error when trying to refresh the token we should be logged out
    } else if (error.response?.status === 401 || originalRequest?.url === '/refresh-token') {
      if (timeoutId) clearTimeout(timeoutId);
      /* istanbul ignore next */
      if (disableAuth) return;
      // clear tokens from storage as neither are valid
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.replace(`${window.location.origin}/login`);
      throw new Error(error.message);
    } else {
      const errorResponse = error.response?.data || {};
      if (Object.hasOwn(errorResponse, 'message') && Object.hasOwn(errorResponse, 'details')) {
        const err = errorResponse as DetailedErrorType;
        throw new DetailedError(err.message, err.details);
      } else {
        const errorObject = error.response?.data as ErrorWithMessage;
        console.log('apiClient error: ', errorObject);
        throw new Error(errorObject?.message || error.message);
      }
    }
  };

const createAPIClient = (apiUrl: string, disableAuth: boolean, queryClient: QueryClient) => {
  const apiClient = axios.create({
    baseURL: apiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 30000,
  });

  apiClient.interceptors.request.use(requestInterceptor(apiClient));
  apiClient.interceptors.response.use((response: AxiosResponse) => {
    clearTimeout(timeoutId);
    return response;
  }, errorResponseInterceptor(apiClient, disableAuth, queryClient));

  return apiClient;
};

export default createAPIClient;
